import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  X,
  Send,
  User,
  Bot,
  Minus,
  Maximize2,
  Clock,
  Sparkles,
  ChevronDown
} from 'lucide-react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const LiveChat = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: "👋 Hi there! Welcome to Ovalu Support. How can I help you today?",
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const getBotResponse = (text) => {
    const input = text.toLowerCase();

    const responses = [
      {
        keywords: ['cycle', 'period', 'ovulation', 'menstrual', 'track'],
        options: [
          "🩸 Your menstrual cycle is important to track! You can use our Cycle Calculator to predict your next period, ovulation, and fertile days. Stay consistent for best results. ✨",
          "Hey! Tracking your cycle is a major step in taking control of your health. 📅 Our calculator can handle the math for you! Want to try it? 💖",
          "Knowledge is power! 🩸 By tracking your cycle, you can better understand your body's unique patterns. Check out our calculator to get started! 🚀"
        ]
      },
      {
        keywords: ['pregnant', 'pregnancy', 'baby', 'conception'],
        options: [
          "🤰 That's wonderful! Ovalu has a dedicated Pregnancy Mode that provides week-by-week updates on your baby's development. You can switch to Pregnancy Journey Mode in Ovalu to track your baby’s development, health tips, and milestones. ✨",
          "Sending you so much love on this journey! 💖 Our Pregnancy Mode is designed to be your companion throughout every milestone. Have you enabled it in your settings? 🤰",
          "Congratulations! 🎈 This is such a special time. Ovalu can help you track every stage of your pregnancy with expert tips and insights. 🤱✨"
        ]
      },
      {
        keywords: ['help', 'support', 'bug', 'error', 'issue', 'problem'],
        options: [
          "I'm sorry you're experiencing an issue. 🛠️ I've logged this for our support team to review. You can also file a formal bug report using the 'Bug Report' option in the sidebar. 📮",
          "Oh no! Let's get that fixed for you. 🔧 I've alerted our tech team. In the meantime, you can file a formal report if you haven't already. 📝",
          "We're on it! 🚀 Our support team has been notified. We appreciate your patience while we investigate this for you. 💖"
        ]
      },
      {
        keywords: ['health', 'pain', 'doctor', 'symptom', 'cramp', 'bleeding', 'pregnant'],
        options: [
          "⚠️ While I can provide general health information, if you're experiencing severe symptoms, please consult a healthcare professional. I can help you find general resources on Ovalu in the meantime. 💊",
          "Your health is our priority. 💖 If you're feeling unwell, we always recommend speaking with a doctor. We have some general wellness tips in the insights section! ✨",
          "Take care of yourself! 🌸 If something doesn't feel right, please seek medical advice. We're here to support your tracking journey once you're feeling better. 🍵"
        ]
      },
      {
        keywords: ['hello', 'hi', 'hey', 'greetings'],
        options: [
          "👋 Hi there! I'm your Ovalu Assistant. I can help you with cycle tracking, pregnancy information, or technical support. What's on your mind? ✨",
          "Hey! Great to see you today. 💖 What can I help you discover about Ovalu today?",
          "Welcome back! 👋 I'm here to help you make the most of your health journey. What would you like to explore first? 🚀"
        ]
      },
      {
        keywords: ['Ovalu', 'ovalu', 'ovalu.app'],
        options: [
          "Ovalu is a comprehensive menstrual health platform that helps women track their menstrual cycle, predict ovulation, and manage their reproductive health. ✨",
          "Think of Ovalu as your personal health companion! 💖 We provide personalized insights and tracking for your unique journey.",
          "We're here to empower women through data and insights! 🚀 Ovalu makes managing your reproductive health simpler and smarter."
        ]
      },
      {
        keywords: ['Thank you', 'thanks'],
        options: [
          "💬 You're welcome! Enjoy your life with Ovalu ✨",
          "Anytime! We're always here to help. 💖",
          "Happy to help! Have a wonderful day! 🌸"
        ]
      }
    ];

    const match = responses.find(r => r.keywords.some(k => input.includes(k)));
    if (match) {
      const randomIndex = Math.floor(Math.random() * match.options.length);
      return match.options[randomIndex];
    }

    const fallbacks = [
      "💬 Thanks for your message! Our health experts will review and get back to you soon. Meanwhile, you can explore Ovalu features for insights. ✨",
      "I've shared your message with our team! 💖 They'll be in touch soon. Is there anything else I can help you find in the app? 🔍",
      "Thanks for reaching out! 👋 One of our experts will join this chat soon. While you wait, check out our latest health insights! 🚀"
    ];
    return fallbacks[Math.floor(Math.random() * fallbacks.length)];
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: inputValue,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    const responseText = getBotResponse(currentInput);
    const typingTime = Math.min(Math.max(responseText.length * 20, 1000), 3000);
    setInputValue('');
    setIsTyping(true);

    // Smart Navigation Logic
    const NAVIGATION_MAP = {
      'calculator': '/cycle-calculator',
      'calendar': '/cycle-calculator',
      'track': '/cycle-calculator',
      'cycle': '/cycle-calculator',
      'period': '/cycle-calculator',
      'bug': '/help',
      'report': '/help',
      'contact': '/help',
      'about': '/about',
      'donate': '/contribute',
      'contribute': '/contribute',
      'help girls': '/contribute',
      'mission': '/contribute',
      'awareness': '/contribute',
      'impact': '/contribute'
    };

    const targetKey = Object.keys(NAVIGATION_MAP).find(key => currentInput.toLowerCase().includes(key));

    if (targetKey) {
      setTimeout(() => {
        navigate(NAVIGATION_MAP[targetKey]);
        setIsOpen(false);
        toast.success(`Redirecting to ${targetKey} page...`, {
          icon: '🚀',
          duration: 2000
        });
      }, 1500);
    }

    setTimeout(() => {
      setIsTyping(false);
      const botMessage = {
        id: messages.length + 2,
        type: 'bot',
        text: responseText,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, botMessage]);
    }, typingTime);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-[#FF6B9D] to-[#FFB6C1] rounded-full shadow-[0_8px_30px_rgb(255,107,157,0.3)] flex items-center justify-center hover:scale-110 transition-all duration-300 z-[100] group"
      >
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
        <MessageSquare className="w-8 h-8 text-white transition-transform group-hover:rotate-12" />
      </button>
    );
  }

  return (
    <div className={cn(
      "fixed right-6 z-[100] transition-all duration-300 flex flex-col shadow-2xl overflow-hidden",
      isMinimized ? "bottom-6 w-80 h-14" : "bottom-6 w-96 h-[600px] rounded-[2rem]",
      "bg-white/95 backdrop-blur-xl border border-pink-100"
    )}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF6B9D] to-[#FFB6C1] p-4 flex items-center justify-between shadow-lg h-14 shrink-0 px-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm">Ovalu Support</h3>
            <div className="flex items-center gap-1.5 overflow-hidden">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-white/80 text-[10px] whitespace-nowrap">Online • Agents available soon</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isMinimized ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMinimized(false)}
              className="w-8 h-8 hover:bg-white/10 text-white rounded-lg"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMinimized(true)}
              className="w-8 h-8 hover:bg-white/10 text-white rounded-lg"
            >
              <Minus className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 hover:bg-white/10 text-white rounded-lg"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Chat Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-pink-50/30 to-white"
          >
            {/* Availability Banner */}
            <div className="bg-blue-50/50 border border-blue-100 p-2.5 rounded-2xl flex items-start gap-2.5 mb-6">
              <Clock className="w-4 h-4 text-blue-600 mt-0.5" />
              <div className="text-[11px] text-blue-800 leading-relaxed">
                <span className="font-bold underline">Live Hours:</span> 9:00 AM - 6:00 PM IST.<br />
                Messages sent outside these hours will be addressed by email.
              </div>
            </div>

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "flex flex-col max-w-[85%] group transform transition-all duration-300 translate-y-0 opacity-100",
                  msg.type === 'user' ? "ml-auto items-end" : "mr-auto items-start"
                )}
              >
                <div className={cn(
                  "px-4 py-3 text-sm shadow-sm",
                  msg.type === 'user'
                    ? "bg-[#FF6B9D] text-white rounded-2xl rounded-tr-none shadow-pink-100"
                    : "bg-white border border-pink-50 text-gray-800 rounded-2xl rounded-tl-none"
                )}>
                  {msg.text}
                </div>
                <span className="text-[10px] text-gray-400 mt-1 px-1">{msg.time}</span>
              </div>
            ))}

            {isTyping && (
              <div className="flex items-center gap-2 p-2 bg-gray-50/50 rounded-2xl w-fit">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce"></span>
                  <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                  <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <form
            onSubmit={handleSendMessage}
            className="p-4 bg-white border-t border-pink-50 flex items-center gap-2 shrink-0 h-20"
          >
            <div className="relative flex-1 group">
              <Input
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="pr-10 border-pink-100 focus:border-[#FF6B9D] rounded-xl h-12 text-sm bg-gray-50/50 focus:bg-white transition-all shadow-inner"
              />
              <Sparkles className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-200 group-focus-within:text-[#FF6B9D] transition-colors" />
            </div>
            <Button
              type="submit"
              size="icon"
              disabled={!inputValue.trim()}
              className="bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white rounded-xl w-12 h-12 shrink-0 shadow-lg active:scale-95 transition-all disabled:opacity-50 disabled:scale-100"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </>
      )}
    </div>
  );
};

export default LiveChat;
